import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';

import routes from 'js/core_pre_webpack/routes';
import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';
import { Icon } from 'js/design_system/Icon';
import PremiumLock from 'js/premium/PremiumLock';
import { trackClick } from 'js/utils/trackClick';
import type { RegistrationParams } from 'js/login_modal/types';

import { HeroThumbnailStack } from 'js/liswp_blocks/shared/HeroThumbnailStack';
import {
  PackPreviewRows,
  PackPreviewFade,
} from 'js/liswp_blocks/shared/PackPreviewRows';
import { PdfMiniChip } from 'js/liswp_blocks/shared/PdfMiniChip';
import { Work as SidebarWork } from 'js/liswp/Sidebar/hooks/types';
import { authorLine } from 'js/liswp_blocks/shared/authorLine';
import { formatStat } from 'js/liswp_blocks/shared/formatStat';
import {
  PACK_CLICK_SOURCE,
  PDF_CLICK_SOURCE,
  IMPRESSION_SOURCE,
  THUMBNAIL_CLICK_SOURCE,
  TAB_PACK_CLICK_SOURCE,
  TAB_PDF_CLICK_SOURCE,
} from 'js/single_work/loswp/shared/clickSources';
import { HeroWorkCardHeader } from 'js/single_work/loswp/shared/HeroWorkCardHeader';
import { useWorkViews } from 'js/single_work/loswp/shared/useWorkViews';
import { packCheckoutOpts } from 'js/single_work/loswp/download_modal/checkoutOpts';
import { openDownloadModal } from 'js/single_work/loswp/download_modal/openDownloadModal';

type Tab = 'pack' | 'pdf';

type PackRow = {
  id: number;
  title: string;
  image?: string;
  authors: Array<{ displayName: string }>;
  publicationYear?: number;
  viewCount?: number;
};

export type LoswpTabsHeroProps = {
  workId: number;
  paperTitle: string;
  authors: Array<{ displayName: string }>;
  publicationName?: string;
  publicationYear?: number;
  thumbnailUrl?: string;
  attachmentId?: number;
  pack: {
    priceCopy: string;
    totalCount: number;
    relatedWorks: PackRow[];
  };
  headerVariant?: 'centered' | 'workCard';
  clickableThumbnail?: boolean;
  signupModalVariant?: 'paperCard' | 'packSignup';
  packRowMeta?: boolean;
};

const tabState = (v2: boolean, active: boolean) => {
  if (v2) return active ? 'v2active' : 'v2inactive';
  return active ? 'active' : 'inactive';
};

const Section = styled.section`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-top: var(--spacing-md-40, 40px);
  color: var(--text-primary, #141413);

  @include breakpoint(small, medium) {
    padding: var(--spacing-md-40, 40px) var(--spacing-xs-16, 16px) 0;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs-8, 8px);
  width: 100%;
  max-width: 862px;
  padding-bottom: var(--spacing-sm-24, 24px);
  text-align: center;
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-family:
    var(--type-font-family-sans-serif, 'DM Sans'), Helvetica, Arial, sans-serif;
  font-size: var(--type-heading-md-size, 28px);
  line-height: var(--type-heading-md-line-height, 36px);
  font-weight: var(--type-heading-md-font-weight, 600);
  color: var(--text-primary, #141413);
`;

const WorkCardHeaderWrap = styled.div`
  width: 100%;
  max-width: 1004px;
`;

const TabsCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 820px;
`;

const TabsRow = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: var(--spacing-sm-24, 24px);

  @include breakpoint(small, medium) {
    gap: var(--spacing-xs-16, 16px);
  }
`;

const TabButton = styled.button<{
  state: 'active' | 'inactive' | 'v2active' | 'v2inactive';
}>`
  @import 'stylesheets/design_system/breakpoint';

  @mixin tab-inactive {
    background: var(--background-white, #ffffff);
    border: 1px solid var(--stroke-medium, #535366);
    border-bottom: none;
  }

  @mixin tab-active {
    position: relative;
    z-index: 1;
    height: 56px;
    margin-top: 2px;
    background: var(--background-light-blue, #eef2f9);
    border: 2px solid var(--primary-brand, #0645b1);
    border-bottom: none;

    &::after {
      content: '';
      position: absolute;
      right: 0;
      bottom: -2px;
      left: 0;
      height: 2px;
      background: var(--background-light-blue, #eef2f9);
    }
  }

  @mixin tab-v2-pill {
    @include breakpoint(small, medium) {
      flex: 1 1 164px;
      max-width: 164px;
      padding: var(--spacing-xs-16, 16px);
    }
  }

  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs-12, 12px);
  height: 58px;
  width: 286px;
  padding: var(--spacing-xs-16, 16px) var(--spacing-sm-32, 32px);
  cursor: pointer;
  border-radius: 12px 12px 0 0;
  color: var(--text-primary, #141413);

  @include breakpoint(small, medium) {
    flex: 1 0 0;
    width: auto;
    min-width: 0;
    padding: var(--spacing-xs-16, 16px);
  }

  &.state-inactive {
    @include tab-inactive;
  }

  &.state-active {
    @include tab-active;
  }

  &.state-v2inactive {
    @include tab-inactive;
    @include tab-v2-pill;
  }

  &.state-v2active {
    @include tab-active;
    @include tab-v2-pill;

    @include breakpoint(small, medium) {
      height: 57px;
      border: 1px solid var(--stroke-medium, #535366);
      border-bottom: none;

      &::after {
        bottom: -1px;
        height: 1px;
      }
    }
  }
`;

const TabLabel = styled.span<{ chrome?: 'v2' }>`
  @import 'stylesheets/design_system/breakpoint';

  font-family:
    var(--type-font-family-sans-serif, 'DM Sans'), Helvetica, Arial, sans-serif;
  font-size: var(--type-body-lg-bold-size, 20px);
  line-height: var(--type-body-lg-bold-line-height, 32px);
  font-weight: var(--type-body-lg-bold-font-weight, 600);
  color: var(--text-primary, #141413);
  white-space: nowrap;

  &.chrome-v2 {
    @include breakpoint(small, medium) {
      font-size: var(--type-body-md-bold-size, 16px);
      line-height: var(--type-body-md-bold-line-height, 20px);
    }
  }
`;

const TabLabelFull = styled.span`
  @import 'stylesheets/design_system/breakpoint';

  @include breakpoint(small, medium) {
    display: none;
  }
`;

const TabLabelShort = styled.span`
  @import 'stylesheets/design_system/breakpoint';

  display: none;

  @include breakpoint(small, medium) {
    display: inline;
  }
`;

const Panel = styled.div<{ chrome?: 'v2' }>`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: var(--spacing-sm-32, 32px) var(--spacing-md-40, 40px);
  cursor: pointer;
  background: var(--background-light-blue, #eef2f9);
  border: 2px solid var(--primary-brand, #0645b1);
  border-radius: var(--border-radius-radius-md, 8px);

  @include breakpoint(small, medium) {
    padding: var(--spacing-sm-24, 24px) var(--spacing-xs-16, 16px);
    border-radius: 0 0 var(--border-radius-radius-md, 8px)
      var(--border-radius-radius-md, 8px);
  }

  &.chrome-v2 {
    @include breakpoint(small, medium) {
      padding: var(--spacing-xs-16, 16px);
      border: 1px solid var(--stroke-medium, #535366);
    }
  }
`;

const PreviewClip = styled.div<{ size?: 'v2' }>`
  @import 'stylesheets/design_system/breakpoint';

  position: relative;
  width: 100%;
  height: 510px;
  overflow: hidden;

  @include breakpoint(small, medium) {
    height: 504px;
  }

  &.size-v2 {
    height: 598px;

    @include breakpoint(small, medium) {
      height: 626px;
    }
  }
`;

const CurrentCardWrap = styled.div<{ spacing?: 'v2' }>`
  position: relative;
  width: 100%;
  padding-top: 14px;

  &.spacing-v2 {
    padding: var(--spacing-sm-24, 24px) 0;
  }
`;

const Chip = styled.div<{ offset?: 'v2' }>`
  position: absolute;
  top: 0;
  left: var(--spacing-sm-24, 24px);
  z-index: 1;
  display: flex;
  align-items: center;
  padding: var(--spacing-xs-4, 4px) var(--spacing-xs-8, 8px);
  background: var(--primary-brand, #0645b1);
  border-radius: 7px;

  &.offset-v2 {
    top: 9px;
  }
`;

const ChipText = styled.span`
  font-family:
    var(--type-font-family-sans-serif, 'DM Sans'), Helvetica, Arial, sans-serif;
  font-size: var(--type-body-sm-bold-size, 14px);
  line-height: var(--type-body-sm-bold-line-height, 20px);
  font-weight: var(--type-body-sm-bold-font-weight, 600);
  color: var(--text-white, #ffffff);
  white-space: nowrap;
`;

const CurrentCard = styled.div<{ chrome?: 'v2' }>`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  align-items: center;
  gap: var(--spacing-xs-16, 16px);
  width: 100%;
  padding: var(--spacing-sm-24, 24px) 20px var(--spacing-xs-16, 16px);
  border: 2px solid #2a5dc1;
  border-radius: var(--border-radius-radius-md, 8px);

  &.chrome-v2 {
    @include breakpoint(small, medium) {
      align-items: flex-start;
    }
  }
`;

const CurrentThumb = styled.div<{ chip?: 'on' }>`
  flex-shrink: 0;
  width: 64px;
  height: 87px;

  &.chip-on {
    position: relative;
  }
`;

const CurrentInfo = styled.div`
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  min-width: 0;
`;

const CurrentTitle = styled.p`
  margin: 0;
  padding-bottom: var(--spacing-xs-4, 4px);
  font-family:
    var(--type-font-family-sans-serif, 'DM Sans'), Helvetica, Arial, sans-serif;
  font-size: var(--type-body-md-bold-size, 16px);
  line-height: var(--type-body-md-bold-line-height, 20px);
  font-weight: var(--type-body-md-bold-font-weight, 600);
  color: var(--text-primary, #141413);
`;

const CtaBar = styled.div<{ chrome?: 'v2' }>`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-top: var(--spacing-sm-24, 24px);
  padding-left: var(--spacing-xs-16, 16px);
  border-top: 1px solid var(--brand-colors-neutral-500, #c8c8cf);

  @include breakpoint(small, medium) {
    padding-left: 0;
  }

  &.chrome-v2 {
    @include breakpoint(small, medium) {
      padding-top: var(--spacing-xs-16, 16px);
      border-top-color: var(--stroke-medium, #535366);
    }
  }
`;

const PriceRow = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  align-items: center;
  gap: var(--spacing-xs-8, 8px);
  font-family:
    var(--type-font-family-mono, 'IBM Plex Mono'), 'Courier New', monospace;
  font-size: 20px;
  font-weight: 500;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  color: var(--text-primary, #141413);
  white-space: nowrap;

  @include breakpoint(small, medium) {
    display: none;
  }
`;

const CtaDesktop = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  margin-left: auto;

  @include breakpoint(small, medium) {
    display: none;
  }
`;

const CtaMobile = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: none;

  @include breakpoint(small, medium) {
    display: block;
    width: 100%;
  }
`;

const LoswpTabsHero: React.FC<LoswpTabsHeroProps> = ({
  workId,
  paperTitle,
  authors,
  publicationName,
  publicationYear,
  thumbnailUrl,
  attachmentId,
  pack,
  headerVariant = 'centered',
  clickableThumbnail = false,
  signupModalVariant = 'paperCard',
  packRowMeta = false,
}) => {
  const hasPack = pack.totalCount > 0;
  const [tab, setTab] = useState<Tab>(hasPack ? 'pack' : 'pdf');
  const workCardHeader = headerVariant === 'workCard';

  const rowViews = useWorkViews(
    pack.relatedWorks.map((row) => row.id),
    Boolean(packRowMeta),
  );

  useEffect(() => {
    trackClick({
      source: IMPRESSION_SOURCE,
      entityId: workId,
      extra: { source_work_id: workId },
    });
  }, [workId]);

  const workUrl = routes.show_work_url({ id: workId });

  const openPaperCardModal = async (
    postLoginRedirectUrl: string,
    registrationParams: RegistrationParams,
  ) => {
    const { openLoginModal } = await import('js/login_modal/LoginModalManager');
    openLoginModal({
      postLoginRedirectUrl,
      workTitle: paperTitle,
      authorName: authors[0]?.displayName,
      workThumbnailUrl: thumbnailUrl,
      workYear: publicationYear,
      headerVariant: 'yourPaperCard',
      ctaLayout: 'googleOnlyExpander',
      configuredText: { buttonsPage: { subheader: '' } },
      registrationParams,
    });
  };

  const openSignupModal = (mode: 'pdf_only' | 'pdf_pack') => {
    if (signupModalVariant === 'packSignup') {
      openDownloadModal({
        mode,
        workId,
        workTitle: paperTitle,
        authors,
        publicationName,
        publicationYear,
        thumbnailUrl,
        attachmentId,
        pack: {
          priceCopy: pack.priceCopy,
          totalCount: pack.totalCount,
          relatedWorks: pack.relatedWorks,
          rowViews,
        },
      });
      return;
    }

    if (mode === 'pdf_pack') {
      const checkoutOpts = packCheckoutOpts(workId);
      openPaperCardModal(routes.checkout_url(checkoutOpts), {
        user_source: 'download',
        checkout_opts: checkoutOpts,
      });
      return;
    }

    const redirect = new URL(workUrl, window.location.origin);
    redirect.searchParams.set('auto', 'download');
    openPaperCardModal(redirect.toString(), {
      user_source: 'download',
      doc_id: attachmentId,
      auto: 'download',
    });
  };

  const onPackCta = () => {
    trackClick({
      source: PACK_CLICK_SOURCE,
      entityId: workId,
      extra: { source_work_id: workId },
    });
    openSignupModal('pdf_pack');
  };

  const onPdfCta = () => {
    trackClick({
      source: PDF_CLICK_SOURCE,
      entityId: workId,
      extra: { source_work_id: workId },
    });
    openSignupModal('pdf_only');
  };

  const onThumbnailCta = () => {
    trackClick({
      source: THUMBNAIL_CLICK_SOURCE,
      entityId: workId,
      extra: { source_work_id: workId },
    });
    openSignupModal('pdf_only');
  };

  const onTabSelect = (next: Tab) => {
    if (next === tab) return;
    trackClick({
      source: next === 'pack' ? TAB_PACK_CLICK_SOURCE : TAB_PDF_CLICK_SOURCE,
      entityId: workId,
      extra: { source_work_id: workId },
    });
    setTab(next);
  };

  const onPanelClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button, a')) return;
    if (tab === 'pack') {
      onPackCta();
    } else {
      onPdfCta();
    }
  };

  const publicationLine = [publicationYear, publicationName]
    .filter(Boolean)
    .join(', ');

  const relatedCount = pack.totalCount;
  const packCount = relatedCount + 1;
  const packCtaLabel = `Download PDF Pack for ${pack.priceCopy}`;

  const rowYears = packRowMeta
    ? Object.fromEntries(
        pack.relatedWorks
          .filter((row) => row.publicationYear != null)
          .map((row) => [String(row.id), row.publicationYear as number]),
      )
    : undefined;

  const currentCard = (
    <CurrentCard chrome={workCardHeader ? 'v2' : undefined}>
      <CurrentThumb chip={packRowMeta ? 'on' : undefined}>
        <HeroThumbnailStack
          thumbnailUrl={thumbnailUrl}
          hasPdf={false}
          pageBorderColor="var(--stroke-beige-light, #ccc9bc)"
        />
        {packRowMeta && <PdfMiniChip />}
      </CurrentThumb>
      <CurrentInfo>
        <CurrentTitle>{paperTitle}</CurrentTitle>
        <Body size="sm">{authorLine(authors)}</Body>
        {publicationLine && <Body size="sm">{publicationLine}</Body>}
      </CurrentInfo>
    </CurrentCard>
  );

  return (
    <Section>
      {workCardHeader ? (
        <WorkCardHeaderWrap>
          <HeroWorkCardHeader
            workId={workId}
            paperTitle={paperTitle}
            authors={authors}
            publicationName={publicationName}
            publicationYear={publicationYear}
            thumbnailUrl={thumbnailUrl}
            showBookmark
            onThumbnailClick={clickableThumbnail ? onThumbnailCta : undefined}
            thumbnailClickLabel="Download PDF"
            bottomPadding={32}
          />
        </WorkCardHeaderWrap>
      ) : (
        <Header>
          <HeroTitle>{paperTitle}</HeroTitle>
          <Body size="sm">{authorLine(authors)}</Body>
          {publicationLine && <Body size="sm">{publicationLine}</Body>}
        </Header>
      )}

      <TabsCard>
        <TabsRow role="tablist">
          {hasPack && (
            <TabButton
              type="button"
              role="tab"
              aria-selected={tab === 'pack'}
              state={tabState(workCardHeader, tab === 'pack')}
              onClick={() => onTabSelect('pack')}
            >
              <PremiumLock
                width={20}
                circleColor="gold"
                lockColor="transparent"
              />
              <TabLabel chrome={workCardHeader ? 'v2' : undefined}>
                <TabLabelFull>Full PDF Pack for {pack.priceCopy}</TabLabelFull>
                <TabLabelShort>PDF Pack</TabLabelShort>
              </TabLabel>
            </TabButton>
          )}
          <TabButton
            type="button"
            role="tab"
            aria-selected={tab === 'pdf'}
            state={tabState(workCardHeader, tab === 'pdf')}
            onClick={() => onTabSelect('pdf')}
          >
            <Icon name="description" size={28} />
            <TabLabel chrome={workCardHeader ? 'v2' : undefined}>
              PDF only
            </TabLabel>
          </TabButton>
        </TabsRow>

        <Panel
          onClick={onPanelClick}
          chrome={workCardHeader ? 'v2' : undefined}
        >
          {tab === 'pack' ? (
            <PreviewClip size={packRowMeta ? 'v2' : undefined}>
              <CurrentCardWrap spacing={packRowMeta ? 'v2' : undefined}>
                {relatedCount > 0 && (
                  <Chip offset={packRowMeta ? 'v2' : undefined}>
                    <ChipText>
                      Get this PDF + {formatStat(relatedCount)} PDFs
                    </ChipText>
                  </Chip>
                )}
                {currentCard}
              </CurrentCardWrap>
              <PackPreviewRows
                variant="tabs"
                relatedWorks={pack.relatedWorks as unknown as SidebarWork[]}
                rowViews={rowViews}
                rowYears={rowYears}
                thumbChips={packRowMeta}
              />
              {relatedCount > 0 && (
                <PackPreviewFade
                  relatedCount={relatedCount}
                  size={packRowMeta ? 'v2' : undefined}
                />
              )}
            </PreviewClip>
          ) : (
            <CurrentCardWrap spacing={packRowMeta ? 'v2' : undefined}>
              {packRowMeta && (
                <Chip offset="v2">
                  <ChipText>Get this PDF</ChipText>
                </Chip>
              )}
              {currentCard}
            </CurrentCardWrap>
          )}

          <CtaBar chrome={workCardHeader ? 'v2' : undefined}>
            {tab === 'pack' ? (
              <>
                {relatedCount > 0 && (
                  <PriceRow>
                    <span>{pack.priceCopy}</span>
                    <span>·</span>
                    <span>{formatStat(packCount)} PDFs</span>
                  </PriceRow>
                )}
                <CtaDesktop>
                  <Button iconRight="arrow_forward" onClick={onPackCta}>
                    {packCtaLabel}
                  </Button>
                </CtaDesktop>
                <CtaMobile>
                  <Button
                    fullWidth
                    size="sm"
                    iconRight="arrow_forward"
                    onClick={onPackCta}
                  >
                    {packCtaLabel}
                  </Button>
                </CtaMobile>
              </>
            ) : (
              <>
                <CtaDesktop>
                  <Button icon="arrow_downward" onClick={onPdfCta}>
                    Download PDF
                  </Button>
                </CtaDesktop>
                <CtaMobile>
                  <Button
                    fullWidth
                    size="sm"
                    icon="arrow_downward"
                    onClick={onPdfCta}
                  >
                    Download PDF
                  </Button>
                </CtaMobile>
              </>
            )}
          </CtaBar>
        </Panel>
      </TabsCard>
    </Section>
  );
};

export default LoswpTabsHero;
